"use client"

import { useState, useEffect } from "react"
import { ChevronDown, Sparkles, Loader2, ImageIcon, Upload, X, Palette } from "lucide-react"
import { dictionary, Language } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { fileToPngDataUrl } from '../../../utils/imageDataUrl';
import {
  getStorePalettesForStyle,
  type StoreTemplateStyleId,
} from "../store/template-design-options";
interface StepBrandConfigProps {
  loading: boolean
  name: string[]
  context: string[]
  selectedBrandName: string
  selectedBrandContext: string
  selectedBrandLogoImage?: string
  selectedStyleSheet: StoreTemplateStyleId
  selectedPalette: string
  setSelectedBrandName: (name: string) => void
  setSelectedBrandContext: (context: string) => void
  setSelectedBrandLogoImage: (logo?: string) => void
  setSelectedPalette: (palette: string) => void
}

export function StepBrandConfig({
  loading,
  name,
  context,
  selectedBrandName,
  selectedBrandContext,
  selectedBrandLogoImage,
  selectedStyleSheet,
  selectedPalette,
  setSelectedBrandName,
  setSelectedBrandContext,
  setSelectedBrandLogoImage,
  setSelectedPalette,
}: StepBrandConfigProps) {
  const { language } = useAuth();
  const [brandNames, setBrandNames] = useState(name || [])
  const [brandContexts, setBrandContexts] = useState(context || [])
  const [brandNameIndex, setBrandNameIndex] = useState(0)
  const [brandContextIndex, setBrandContextIndex] = useState(0)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [paletteExpanded, setPaletteExpanded] = useState(false)

  useEffect(() => {
    if (name && name.length > 0) {
      setBrandNames(name)
      setSelectedBrandName(name[0])
      setBrandNameIndex(0)
    }
  }, [name])

  useEffect(() => {
    if (context && context.length > 0) {
      setBrandContexts(context)
      setSelectedBrandContext(context[0])
      setBrandContextIndex(0)
    }
  }, [context])

  const generateNewBrandName = () => {
    const currentNames = name && name.length > 0 ? name : brandNames
    if (currentNames.length === 0) return

    const nextIndex = (brandNameIndex + 1) % currentNames.length
    setBrandNameIndex(nextIndex)
    setSelectedBrandName(currentNames[nextIndex])
  }

  const availablePalettes = getStorePalettesForStyle(selectedStyleSheet);
  const selectedPaletteOption = availablePalettes.find((palette) => palette.id === selectedPalette);

  const generateNewBrandContext = () => {
    const currentContexts = context && context.length > 0 ? context : brandContexts
    if (currentContexts.length === 0) return

    const nextIndex = (brandContextIndex + 1) % currentContexts.length
    setBrandContextIndex(nextIndex)
    setSelectedBrandContext(currentContexts[nextIndex])
  }

  const handleLogoUpload = async (file?: File) => {
    if (!file) return;
    try {
      const png = await fileToPngDataUrl(file);
      const fallbackName = file.name.replace(/\.[^/.]+$/, '').trim();
      if (!selectedBrandName.trim() && fallbackName) {
        setSelectedBrandName(fallbackName);
      }
      setSelectedBrandLogoImage(png);
      setLogoError(null);
    } catch (error) {
      console.error('Error processing store logo:', error);
      setLogoError(
        language === 'en'
          ? 'We could not process the logo.'
          : language === 'pt'
            ? 'Nao conseguimos processar o logo.'
            : 'No pudimos procesar el logo.'
      );
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="brand-name" className="text-base font-semibold text-slate-950">
            {dictionary["brand-name"]?.[language as Language] ?? "Nombre de marca"}
          </label>
          <button
            type="button"
            onClick={generateNewBrandName}
            disabled={loading}
            className="inline-flex h-9 items-center justify-center rounded-full bg-slate-100 px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {dictionary["generating"]?.[language as Language] ?? "Generando..."}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {dictionary["regenerate-with-ia"]?.[language as Language] ?? "Regenerar con IA"}
              </>
            )}
          </button>
        </div>
        <input
          id="brand-name"
          type="text"
          className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-fluxi focus:ring-4 focus:ring-blue-100"
          placeholder={dictionary["enter-brand-name"]?.[language as Language] ?? "Ingresa el nombre de tu marca"}
          value={selectedBrandName}
          disabled={loading}
          onChange={(e) => setSelectedBrandName(e.target.value)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <label className="text-base font-semibold text-slate-950">
              Logo de la tienda
            </label>
            <p className="text-sm text-slate-500">
              Fluxi generará las versiones para fondos claros y oscuros desde este logo.
            </p>
          </div>
          {selectedBrandLogoImage && (
            <button
              type="button"
              onClick={() => setSelectedBrandLogoImage(undefined)}
              className="inline-flex h-9 items-center gap-2 rounded-full bg-slate-100 px-3 text-sm font-medium text-slate-700 hover:bg-slate-200"
            >
              <X className="h-4 w-4" />
              Quitar
            </button>
          )}
        </div>
        <div className="grid gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 sm:grid-cols-[160px_1fr] sm:items-center">
          <div className="flex h-24 items-center justify-center rounded-2xl border border-slate-200 bg-white p-3">
            {selectedBrandLogoImage ? (
              <img src={selectedBrandLogoImage} alt="Logo de la tienda" className="max-h-full max-w-full object-contain" />
            ) : (
              <ImageIcon className="h-7 w-7 text-slate-300" />
            )}
          </div>
          <div className="space-y-2">
            <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100">
              <Upload className="h-4 w-4" />
              Subir logo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={loading}
                onChange={(event) => void handleLogoUpload(event.target.files?.[0])}
              />
            </label>
            <p className="text-sm text-slate-500">
              Idealmente usa PNG o SVG con fondo transparente.
            </p>
            {logoError && <p className="text-sm text-red-600">{logoError}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <button
          type="button"
          onClick={() => setPaletteExpanded((expanded) => !expanded)}
          className="flex w-full items-center justify-between gap-4 text-left"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-fluxi">
              <Palette className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h4 className="text-base font-semibold text-slate-950">Color de la tienda</h4>
              <p className="mt-1 text-sm text-slate-500">
                {selectedPalette === "auto"
                  ? "Color de marca detectado automáticamente."
                  : selectedPaletteOption?.name || "Color personalizado"}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-3">
            <span
              className="h-7 w-7 rounded-full ring-1 ring-black/10"
              style={{
                background: selectedPalette === "auto"
                  ? "linear-gradient(135deg,#111827,#2563eb,#f8fafc)"
                  : selectedPaletteOption?.primary || "#2563EB",
              }}
            />
            <ChevronDown className={`h-5 w-5 text-slate-400 transition ${paletteExpanded ? "rotate-180" : ""}`} />
          </div>
        </button>

        {paletteExpanded && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => {
                setSelectedPalette("auto");
                setPaletteExpanded(false);
              }}
              className={`h-10 rounded-full px-4 text-sm font-semibold transition ${
                selectedPalette === "auto"
                  ? "bg-slate-950 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Color de marca
            </button>
            {availablePalettes.map((palette) => (
              <button
                key={palette.id}
                type="button"
                onClick={() => {
                  setSelectedPalette(palette.id);
                  setPaletteExpanded(false);
                }}
                className={`flex h-10 items-center gap-2 rounded-full px-3 text-sm font-medium ring-1 transition ${
                  selectedPalette === palette.id
                  ? "bg-slate-950 text-white ring-slate-950"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                <span
                  className="h-4 w-4 rounded-full ring-1 ring-black/10"
                  style={{ backgroundColor: palette.primary }}
                />
                {palette.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Brand Context Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <label htmlFor="brand-context" className="text-base font-semibold text-slate-950">
              {dictionary["brand-context"]?.[language as Language] ?? "Contexto de marca"}
            </label>
            <span className="text-slate-500 ml-2">{dictionary["brand-context-description"]?.[language as Language] ?? "Cuál es tu misión y qué hace única a tu marca"}</span>
          </div>
          <button
            type="button"
            onClick={generateNewBrandContext}
            disabled={loading}
            className="inline-flex h-9 items-center justify-center rounded-full bg-slate-100 px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {dictionary["generating"]?.[language as Language] ?? "Generando..."}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {dictionary["regenerate-with-ia"]?.[language as Language] ?? "Regenerar con IA"}
              </>
            )}
          </button>
        </div>
        <textarea
          id="brand-context"
          className="h-36 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-[1.6] text-slate-900 shadow-sm outline-none transition focus:border-fluxi focus:ring-4 focus:ring-blue-100"
          placeholder={dictionary["describe-brand"]?.[language as Language] ?? "Describe tu marca: misión, valores, público objetivo y qué la hace única"}
          value={selectedBrandContext}
          disabled={loading}
          onChange={(e) => setSelectedBrandContext(e.target.value)}
        />
      </div>
    </div>
  )
}
